import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { DeniedState, ErrorState, LoadingSkeleton } from '@/shared/feedback';
import { useAsyncResource } from '@/shared/hooks/use-async-resource';
import { isForbidden, toError } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import { DeploymentSettingsForm } from './deployment-settings-form';
import type { UpdatePlatformSettingsValues } from './deployment-settings-types';

/**
 * The deployment's AI settings.
 *
 * The settings and the provider list are loaded SEPARATELY, because they are
 * authorized differently: reading the settings needs
 * `ai.settings.platform.read`, and reading the providers needs
 * `ai.provider.platform.read`. Loading them together would let one refusal
 * hide the other.
 */
export function DeploymentSettingsSection(): ReactNode {
  const settingsLoad = useCallback(() => aiApi.getPlatformSettings(), []);
  const providersLoad = useCallback(() => aiApi.listPlatformProviders(), []);
  const settings = useAsyncResource(settingsLoad);
  const providers = useAsyncResource(providersLoad);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);

  const save = async (values: UpdatePlatformSettingsValues): Promise<void> => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await aiApi.updatePlatformSettings(values);
      settings.reload();
    } catch (cause) {
      setSaveError(toError(cause));
    } finally {
      setIsSaving(false);
    }
  };

  if (settings.isLoading) {
    return <LoadingSkeleton shape="detail" rows={4} label="Loading deployment settings" />;
  }
  if (isForbidden(settings.error)) {
    return (
      <DeniedState
        title="Not permitted for your role"
        message="You cannot read this deployment's AI settings."
      />
    );
  }
  if (settings.error) {
    return (
      <ErrorState
        title="Could not load the deployment settings"
        message={settings.error.message}
        onRetry={settings.reload}
      />
    );
  }

  const current = settings.data;
  if (current === null) {
    return (
      <ErrorState title="Could not load the deployment settings" message="No settings were returned." />
    );
  }

  return (
    <DeploymentSettingsForm
      // Remounting on a new version re-seeds the form from the server's copy
      // without a state-reset effect.
      key={`${current.version}-${current.deploymentCode}`}
      settings={current}
      providers={providers.data ?? []}
      isSaving={isSaving}
      saveError={saveError}
      onSubmit={(values: UpdatePlatformSettingsValues) => {
        void save(values);
      }}
    />
  );
}
